import React from "react";

import { waitPromise } from "../../../../commons/libs/promise";
import { Component } from "../../../../commons/types/component";
import cn from "../../libs/class-name";
import useIntersect from "../../libs/hooks/use-intersect";

interface Props extends Component {
  source?: string;
}

const TRANSPARENT_PIXEL = "data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw==";
const NUM_RETRIES = 10;
const POLL_DELAY = 1000;

const loadImage = (source: string) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = resolve;
    img.onerror = reject;
    img.src = source;
  });

const PdfPage = ({ classNames = [], source = "" }: Props) => {
  const [setNode, observerEntry] = useIntersect({ rootMargin: "300px 300px", once: true });
  const [loaded, setLoaded] = React.useState<boolean>(false);
  const pollRef = React.useRef<string>(undefined);

  const isIntersecting = observerEntry && observerEntry.isIntersecting;

  // Reset `loaded` when the source changes during render. This avoids a synchronous
  // setState inside the polling effect below.
  // See: https://react.dev/reference/react/useState#storing-information-from-previous-renders
  const [prevSource, setPrevSource] = React.useState(source);
  if (prevSource !== source) {
    setPrevSource(source);
    if (!source) {
      setLoaded(false);
    }
  }

  React.useEffect(() => {
    const clearPoll = () => {
      pollRef.current = undefined;
    };

    const pollImage = async (src: string, retries: number) => {
      try {
        await loadImage(source);

        if (pollRef.current !== src) {
          return;
        }

        setLoaded(true);
        clearPoll();
      } catch {
        if (retries > 0) {
          await waitPromise(POLL_DELAY);
          await pollImage(src, retries - 1);
        } else {
          clearPoll();
        }
      }
    };

    const startPoll = (src: string, retries: number) => {
      pollRef.current = src;
      pollImage(src, retries);
    };

    if (pollRef.current !== source) {
      if (source) {
        startPoll(source, NUM_RETRIES);
      } else {
        clearPoll();
      }
    }

    return () => {
      clearPoll();
    };
  }, [isIntersecting, source]);

  return (
    <div className={cn("PdfPage", [{ loaded }], classNames)} ref={setNode}>
      <img className="PdfPage__image" src={loaded && source ? source : TRANSPARENT_PIXEL} alt="" />
    </div>
  );
};

export default PdfPage;
